import { Globe2, History, Map as MapIcon, RotateCw, Video, VideoOff } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import maplibreWorkerUrl from 'maplibre-gl/dist/maplibre-gl-worker.mjs?worker&url'
import { avatarSrc } from '../../shared/avatars'
import { countryFlag } from '../../shared/profileOptions'
import type { GlobeReplay, UserProfile } from '../../shared/types'

const STYLE_URL = 'https://tiles.openfreemap.org/styles/bright'
const REPLAY_DURATION_MS = 24_000

/** Bright gives us the label/road structure, but its vector tiles carry no
 * landcover at low zoom, so continents render flat white. The DataFast look
 * (sandy Sahara, green vegetation) comes from blending a Natural Earth
 * raster underneath, the way the liberty style does — plus a sky-blue ocean,
 * soft red-brown borders, and an atmosphere halo around the globe. */
let cachedGlobeStyle: Promise<Record<string, unknown>> | null = null
function getGlobeStyle(): Promise<Record<string, unknown>> {
  if (!cachedGlobeStyle) {
    cachedGlobeStyle = buildGlobeStyle().catch((error) => {
      cachedGlobeStyle = null
      throw error
    })
  }
  return cachedGlobeStyle
}

async function buildGlobeStyle(): Promise<Record<string, unknown>> {
  const response = await fetch(STYLE_URL)
  if (!response.ok) throw new Error(`style fetch failed: ${response.status}`)
  const style = (await response.json()) as {
    sources: Record<string, unknown>
    layers: Array<{ id: string; type: string; minzoom?: number; paint?: Record<string, unknown> }>
    sky?: Record<string, unknown>
  }
  style.sources.ne2_shaded = {
    type: 'raster',
    tiles: ['https://tiles.openfreemap.org/natural_earth/ne2sr/{z}/{x}/{y}.png'],
    tileSize: 256,
    maxzoom: 6,
  }
  const backgroundIndex = style.layers.findIndex((layer) => layer.type === 'background')
  style.layers.splice(backgroundIndex + 1, 0, {
    id: 'natural_earth',
    type: 'raster',
    // @ts-expect-error loose style JSON, validated by maplibre at load
    source: 'ne2_shaded',
    maxzoom: 7,
    paint: {
      'raster-opacity': ['interpolate', ['exponential', 1.5], ['zoom'], 0, 1, 4, 0.8, 6, 0.2],
      'raster-saturation': 0.25,
      'raster-contrast': 0.05,
    },
  })
  for (const layer of style.layers) {
    if (!layer.paint) continue
    if (layer.id === 'water') layer.paint['fill-color'] = '#8ec3ee'
    if (layer.id.startsWith('boundary')) layer.paint['line-color'] = '#b98a72'
  }
  // the blue halo around the planet; space behind it stays transparent so
  // the starfield canvas underneath shows through
  style.sky = {
    'sky-color': '#66a6e8',
    'horizon-color': '#d6ecff',
    'fog-color': '#9ccbf5',
    'sky-horizon-blend': 0.7,
    'horizon-fog-blend': 0.6,
    'fog-ground-blend': 0.8,
    'atmosphere-blend': ['interpolate', ['linear'], ['zoom'], 0, 1, 6, 0.5, 10, 0],
  }
  return style
}

/** A static starfield: drawn once per resize, costs nothing while idle. */
function drawStars(canvas: HTMLCanvasElement) {
  const host = canvas.parentElement
  if (!host) return
  const rect = host.getBoundingClientRect()
  if (!rect.width || !rect.height) return
  const dpr = window.devicePixelRatio || 1
  canvas.width = Math.round(rect.width * dpr)
  canvas.height = Math.round(rect.height * dpr)
  const ctx = canvas.getContext('2d')
  if (!ctx) return
  ctx.scale(dpr, dpr)
  const count = Math.round((rect.width * rect.height) / 4200)
  for (let i = 0; i < count; i += 1) {
    const x = Math.random() * rect.width
    const y = Math.random() * rect.height
    const big = Math.random() > 0.88
    const radius = big ? 0.9 + Math.random() * 1.1 : 0.3 + Math.random() * 0.7
    const alpha = 0.2 + Math.random() * (big ? 0.75 : 0.5)
    ctx.beginPath()
    ctx.arc(x, y, radius, 0, Math.PI * 2)
    ctx.fillStyle = Math.random() > 0.8 ? `rgba(190,214,255,${alpha})` : `rgba(255,255,255,${alpha})`
    if (big) {
      ctx.shadowColor = 'rgba(180,210,255,0.9)'
      ctx.shadowBlur = 4
    } else {
      ctx.shadowBlur = 0
    }
    ctx.fill()
  }
}

function formatClock(time: number): string {
  return new Date(time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
}

interface PinPerson {
  id: string
  name: string
  color: string
  avatarUrl?: string
  location?: string
  cameraOn?: boolean
  countryCode?: string
  basedIn?: string
  demoVideoUrl?: string
}

interface GlobePanelProps {
  users: UserProfile[]
  streamFor?: (user: UserProfile) => MediaStream | null | undefined
  onSelectUser?: (user: UserProfile) => void
}

type ProjectionKind = 'globe' | 'mercator'

interface GlobeApi {
  setProjection?: (type: ProjectionKind) => void
  setAutoPan?: (on: boolean) => void
  startReplay?: () => Promise<boolean>
  stopReplay?: () => void
  refreshVideos?: () => void
}

const toRadians = (degrees: number) => (degrees * Math.PI) / 180
const toDegrees = (radians: number) => (radians * 180) / Math.PI

/** Great-circle destination: from lat/lng, walk `distance` degrees at `bearing`. */
function destinationPoint(lat: number, lng: number, bearing: number, distance: number): [number, number] {
  const la1 = toRadians(lat)
  const br = toRadians(bearing)
  const d = toRadians(distance)
  const la2 = Math.asin(Math.sin(la1) * Math.cos(d) + Math.cos(la1) * Math.sin(d) * Math.cos(br))
  const lo2 = toRadians(lng) + Math.atan2(
    Math.sin(br) * Math.sin(d) * Math.cos(la1),
    Math.cos(d) - Math.sin(la1) * Math.sin(la2),
  )
  return [toDegrees(la2), toDegrees(lo2)]
}

/** DataFast-style live map: a real labeled world map on a globe projection
 * with a starfield behind it, avatar pins that hide behind the horizon,
 * live-stream peeks on hover, a 2D/3D toggle, auto-panning, and an
 * icons-only replay of the last 8 hours. */
export function GlobePanel({ users, streamFor, onSelectUser }: GlobePanelProps) {
  const mount = useRef<HTMLDivElement>(null)
  const stars = useRef<HTMLCanvasElement>(null)
  const api = useRef<GlobeApi>({})
  const streamForRef = useRef(streamFor)
  streamForRef.current = streamFor
  const onSelectUserRef = useRef(onSelectUser)
  onSelectUserRef.current = onSelectUser
  const [projection, setProjection] = useState<ProjectionKind>('globe')
  const projectionRef = useRef(projection)
  projectionRef.current = projection
  const [autoPan, setAutoPan] = useState(false)
  const [replaying, setReplaying] = useState(false)
  const [showVideos, setShowVideos] = useState(true)
  const showVideosRef = useRef(showVideos)
  showVideosRef.current = showVideos
  const [replayLabel, setReplayLabel] = useState<string | null>(null)
  const positioned = useMemo(
    () => users.filter((user) => Number.isFinite(user.latitude) && Number.isFinite(user.longitude)),
    [users],
  )
  const positionedRef = useRef(positioned)
  positionedRef.current = positioned
  const syncMarkersRef = useRef<((people: UserProfile[]) => void) | null>(null)

  useEffect(() => {
    const canvas = stars.current
    if (!canvas) return
    drawStars(canvas)
    const observer = new ResizeObserver(() => drawStars(canvas))
    if (canvas.parentElement) observer.observe(canvas.parentElement)
    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    const element = mount.current
    if (!element) return
    let destroyed = false
    let cleanup: (() => void) | null = null

    void Promise.all([
      import('maplibre-gl'),
      import('maplibre-gl/dist/maplibre-gl.css'),
      getGlobeStyle(),
    ]).then(([maplibregl, , globeStyle]) => {
      if (destroyed || !element.isConnected) return
      // maplibre spawns its tile-decoding worker from a URL it derives at
      // runtime; hand it the chunk our bundler actually emits, otherwise
      // tiles silently never decode and the ocean stays blank.
      maplibregl.setWorkerUrl(maplibreWorkerUrl)

      const first = positionedRef.current[0]
      const target: [number, number] = first
        ? [first.longitude as number, first.latitude as number]
        : [8.54, 47.37]
      // land directly on the room at working size — the fly-in-from-space
      // intro got old fast once the globe became a view you switch to often
      const map = new maplibregl.Map({
        container: element,
        style: globeStyle as never,
        center: target,
        zoom: 2.1,
        minZoom: 1.1,
        maxZoom: 8,
        // credits live in the legal fine print (privacy page), not on the map
        attributionControl: false,
      })
      if (import.meta.env.DEV) (window as unknown as { __globeMap: unknown }).__globeMap = map
      map.on('style.load', () => {
        map.setProjection({ type: projectionRef.current })
      })

      // hovering a pin peeks at the person: live stream if their camera is
      // on and subscribed, otherwise their avatar, large
      // pins show the live stream directly when the video toggle is on;
      // otherwise (or without a stream) the comic avatar
      const fillAvatar = (avatar: HTMLElement, person: PinPerson) => {
        avatar.replaceChildren()
        const stream = showVideosRef.current && person.cameraOn
          ? streamForRef.current?.(person as UserProfile) ?? null
          : null
        if (stream) {
          const video = document.createElement('video')
          video.autoplay = true
          video.muted = true
          video.playsInline = true
          video.srcObject = stream
          avatar.appendChild(video)
          return
        }
        if (showVideosRef.current && person.cameraOn && person.demoVideoUrl) {
          const video = document.createElement('video')
          video.autoplay = true
          video.muted = true
          video.loop = true
          video.playsInline = true
          video.src = person.demoVideoUrl
          // browsers sometimes pause autoplay media; keep the loop running
          video.addEventListener('pause', () => { void video.play().catch(() => undefined) })
          video.addEventListener('canplay', () => { void video.play().catch(() => undefined) })
          avatar.appendChild(video)
          return
        }
        const img = document.createElement('img')
        img.src = avatarSrc(person)
        img.alt = ''
        avatar.appendChild(img)
      }

      const avatarFills: Array<() => void> = []
      const buildPin = (person: PinPerson, onClick?: () => void) => {
        const pin = document.createElement('div')
        pin.className = 'globe-pin'
        const avatar = document.createElement('span')
        avatar.className = 'globe-pin-avatar'
        avatar.style.setProperty('--avatar', person.color)
        fillAvatar(avatar, person)
        avatarFills.push(() => fillAvatar(avatar, person))
        const label = document.createElement('small')
        const flag = person.basedIn || person.countryCode
        const flagSuffix = flag ? ` ${countryFlag(flag)}` : ''
        label.textContent = (person.location ? `${person.name} · ${person.location}` : person.name) + flagSuffix
        pin.append(avatar, label)
        if (onClick) {
          pin.classList.add('clickable')
          pin.addEventListener('click', (event) => { event.stopPropagation(); onClick() })
        }
        return pin
      }

      // presence ticks every few seconds; rebuilding the whole map for each
      // one made the globe reset constantly. The map now lives once and only
      // the markers sync.
      let markers: Array<InstanceType<typeof maplibregl.Marker>> = []
      const syncMarkers = (people: UserProfile[]) => {
        markers.forEach((marker) => marker.remove())
        avatarFills.length = 0
        markers = people.map((user) => {
          // opacityWhenCovered hides pins that rotate behind the globe horizon
          const marker = new maplibregl.Marker({ element: buildPin(user, () => onSelectUserRef.current?.(user as UserProfile)), opacityWhenCovered: '0' })
            .setLngLat([user.longitude as number, user.latitude as number])
            .addTo(map)
          return marker
        })
        resolveLabels()
      }

      // Labels never cover each other: walk the pins in order, the first label
      // in a spot stays, colliders hide (hovering a pin brings its label back).
      function resolveLabels() {
        const accepted: DOMRect[] = []
        for (const marker of markers) {
          const pin = marker.getElement()
          const label = pin.querySelector('small')
          if (!label) continue
          pin.classList.remove('label-hidden')
          if (pin.style.opacity === '0') continue // behind the horizon
          const rect = label.getBoundingClientRect()
          const collides = accepted.some((other) =>
            rect.left < other.right && rect.right > other.left &&
            rect.top < other.bottom && rect.bottom > other.top)
          if (collides) pin.classList.add('label-hidden')
          else accepted.push(rect)
        }
      }

      // The map's own place names render squeezed along the sphere's edge.
      // Symbols have no screen-space filter (and this build's 'distance'
      // expression is inert), so it is done by hand: project every label
      // point in the loaded tiles, and names landing outside 84% of the
      // sphere's radius go onto a per-layer hide list. Refreshed after each
      // movement.
      const baseFilters = new Map<string, unknown>()
      const applyEdgeFilter = () => {
        const style = map.getStyle()
        if (!style || sphereRadius < 40) return
        const limit = sphereRadius * 0.84
        const halfX = element.clientWidth / 2
        const halfY = element.clientHeight / 2
        const groups = new Map<string, { source: string; sourceLayer?: string; layerIds: string[] }>()
        for (const layer of style.layers) {
          if (layer.type !== 'symbol') continue
          const source = (layer as { source?: string }).source
          if (!source) continue
          const sourceLayer = (layer as { 'source-layer'?: string })['source-layer']
          const key = `${source}|${sourceLayer ?? ''}`
          const group = groups.get(key) ?? { source, sourceLayer, layerIds: [] }
          group.layerIds.push(layer.id)
          groups.set(key, group)
        }
        for (const group of groups.values()) {
          let features: Array<{ properties?: Record<string, unknown> | null; geometry: { type: string; coordinates: unknown } }> = []
          try {
            features = map.querySourceFeatures(group.source, group.sourceLayer ? { sourceLayer: group.sourceLayer } : undefined)
          } catch { continue }
          const hidden = new Set<string>()
          for (const feature of features) {
            const name = feature.properties?.name
            if (typeof name !== 'string' || !name) continue
            if (feature.geometry.type !== 'Point') continue
            const projected = map.project(feature.geometry.coordinates as [number, number])
            const distance = Math.hypot(projected.x - halfX, projected.y - halfY)
            if (distance > limit) hidden.add(name)
          }
          const away = ['!', ['in', ['get', 'name'], ['literal', [...hidden]]]]
          for (const id of group.layerIds) {
            if (!baseFilters.has(id)) baseFilters.set(id, map.getFilter(id) ?? null)
            const base = baseFilters.get(id)
            try {
              map.setFilter(id, (base ? ['all', base, away] : away) as never)
            } catch { /* keep the layer's own filter */ }
          }
        }
      }
      map.on('moveend', applyEdgeFilter)
      // at 'load' the label tiles are usually not queryable yet — run once
      // more when rendering first settles, movement keeps it fresh after
      map.once('idle', applyEdgeFilter)

      syncMarkers(positionedRef.current)
      syncMarkersRef.current = syncMarkers

      // The map draws its place names squeezed along the sphere's edge — ugly.
      // A soft atmosphere ring covers the last few percent of the limb and
      // fades into a glow, so nothing garbled survives near the horizon. The
      // sphere is always centered; its pixel radius is measured by projecting
      // points outward along one great circle and keeping the farthest.
      const limbEl = element.parentElement?.querySelector<HTMLElement>('.globe-limb') ?? null
      const starsEl = element.parentElement?.querySelector<HTMLElement>('.globe-stars') ?? null
      let sphereRadius = 0
      const paintSpace = () => {
        const center = map.getCenter()
        const halfX = element.clientWidth / 2
        const halfY = element.clientHeight / 2
        let radius = 0
        for (let step = 55; step <= 89; step += 2) {
          const [lat, lng] = destinationPoint(center.lat, center.lng, 90, step)
          const projected = map.project([lng, lat])
          const distance = Math.hypot(projected.x - halfX, projected.y - halfY)
          if (Number.isFinite(distance) && distance < 6000 && distance > radius) radius = distance
        }
        sphereRadius = radius
        if (limbEl) {
          if (radius > 40) {
            const glow = 140
            const size = 2 * (radius + glow)
            limbEl.style.width = `${size}px`
            limbEl.style.height = `${size}px`
            // a BRIGHT atmospheric rim, like the earth photographed from
            // space: the last few percent wash out into blue-white light
            // (no black seam, no blur), then the glow falls off outside
            // faint and wide: a whisper of light at the limb, then a long
            // outward tail — strength lives in these alphas
            limbEl.style.background = `radial-gradient(circle, rgba(180,208,255,0) ${radius * 0.965}px, rgba(190,215,255,.16) ${radius * 0.988}px, rgba(205,225,255,.42) ${radius}px, rgba(150,192,252,.22) ${radius + 22}px, rgba(112,166,248,.1) ${radius + 60}px, rgba(96,156,240,0) ${radius + glow}px)`
            limbEl.style.display = ''
          } else {
            limbEl.style.display = 'none'
          }
        }
        // the starfield zooms along with the globe for a bit of depth
        if (starsEl) starsEl.style.transform = `scale(${Math.pow(2, (map.getZoom() - 2.1) * 0.22)})`
      }
      const onMove = () => { paintSpace(); resolveLabels() }
      map.on('move', onMove)
      map.on('resize', onMove)
      map.once('load', onMove)
      onMove()

      // The globe holds still by default; the header button starts a gentle pan.
      let spinning = false
      const pauseSpin = () => {
        if (!spinning) return
        spinning = false
        setAutoPan(false)
      }
      map.on('mousedown', pauseSpin)
      map.on('wheel', pauseSpin)
      map.on('touchstart', pauseSpin)
      const spin = window.setInterval(() => {
        if (!spinning || document.hidden) return
        const center = map.getCenter()
        map.easeTo({ center: [center.lng + 4, center.lat], duration: 2_400, easing: (t: number) => t })
      }, 2_400)

      // ---- icons-only replay of the last 8 hours ----
      let replayFrame = 0
      let replayNote = 0
      const replayActive = new Map<string, InstanceType<typeof maplibregl.Marker>>()
      const setLiveVisible = (visible: boolean) => {
        markers.forEach((marker) => { marker.getElement().style.visibility = visible ? 'visible' : 'hidden' })
      }
      const stopReplay = () => {
        window.cancelAnimationFrame(replayFrame)
        window.clearTimeout(replayNote)
        replayActive.forEach((marker) => marker.remove())
        replayActive.clear()
        setLiveVisible(true)
        setReplaying(false)
        setReplayLabel(null)
      }
      const startReplay = async (): Promise<boolean> => {
        const response = await fetch('/api/room/globe-replay?preview=1', { credentials: 'same-origin' })
        if (!response.ok) return false
        const replay = (await response.json()) as GlobeReplay
        if (!replay.sessions.length) {
          setReplayLabel('Nothing to replay yet')
          replayNote = window.setTimeout(() => setReplayLabel(null), 2_500)
          return false
        }
        spinning = false
        setAutoPan(false)
        setLiveVisible(false)
        setReplaying(true)
        const startedAt = performance.now()
        let shownMinute = ''
        const tick = (frameTime: number) => {
          const progress = Math.min(1, (frameTime - startedAt) / REPLAY_DURATION_MS)
          const clock = replay.since + progress * (replay.now - replay.since)
          for (const session of replay.sessions) {
            const key = `${session.id}:${session.joinedAt}`
            const visible = session.joinedAt <= clock && (session.leftAt === null || session.leftAt > clock)
            const existing = replayActive.get(key)
            if (visible && !existing) {
              const marker = new maplibregl.Marker({ element: buildPin(session), opacityWhenCovered: '0' })
                .setLngLat([session.longitude, session.latitude])
                .addTo(map)
              replayActive.set(key, marker)
            } else if (!visible && existing) {
              existing.remove()
              replayActive.delete(key)
            }
          }
          const minute = formatClock(clock)
          if (minute !== shownMinute) {
            shownMinute = minute
            setReplayLabel(minute)
          }
          if (progress < 1) {
            replayFrame = window.requestAnimationFrame(tick)
          } else {
            replayNote = window.setTimeout(stopReplay, 1_500)
          }
        }
        replayFrame = window.requestAnimationFrame(tick)
        return true
      }

      api.current = {
        setProjection: (type) => { map.setProjection({ type }) },
        setAutoPan: (on) => { spinning = on },
        startReplay,
        stopReplay,
        refreshVideos: () => avatarFills.forEach((fill) => fill()),
      }

      cleanup = () => {
        syncMarkersRef.current = null
        if (starsEl) starsEl.style.transform = ''
        stopReplay()
        window.clearInterval(spin)
        markers.forEach((marker) => marker.remove())
        map.remove()
        api.current = {}
      }
    })

    return () => {
      destroyed = true
      cleanup?.()
    }
    // mount-once: presence changes flow through syncMarkers below
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    syncMarkersRef.current?.(positioned)
  }, [positioned])

  const stage = (
      <div className="globe-stage">
        <canvas className="globe-stars" ref={stars} aria-hidden="true" />
        <div className="globe-map" ref={mount} />
        <div className="globe-limb" aria-hidden="true" />
        <div className="globe-controls">
          <button
            title={projection === 'globe' ? 'Switch to 2D map' : 'Switch to globe'}
            aria-label={projection === 'globe' ? 'Switch to 2D map' : 'Switch to globe'}
            onClick={() => {
              const next: ProjectionKind = projection === 'globe' ? 'mercator' : 'globe'
              setProjection(next)
              api.current.setProjection?.(next)
            }}
          >
            {projection === 'globe' ? <MapIcon /> : <Globe2 />}
          </button>
          <button
            className={autoPan ? 'active' : ''}
            title={autoPan ? 'Stop auto-panning' : 'Start auto-panning'}
            aria-label={autoPan ? 'Stop auto-panning' : 'Start auto-panning'}
            onClick={() => {
              const next = !autoPan
              setAutoPan(next)
              api.current.setAutoPan?.(next)
            }}
          >
            <RotateCw />
          </button>
          <button
            className={showVideos ? 'active' : ''}
            title={showVideos ? 'Show avatars on pins' : 'Show live videos on pins'}
            aria-label={showVideos ? 'Show avatars on pins' : 'Show live videos on pins'}
            onClick={() => {
              const next = !showVideos
              setShowVideos(next)
              showVideosRef.current = next
              api.current.refreshVideos?.()
            }}
          >
            {showVideos ? <Video /> : <VideoOff />}
          </button>
          <button
            className={replaying ? 'active' : ''}
            title={replaying ? 'Stop replay' : 'Replay last 8 hours'}
            aria-label={replaying ? 'Stop replay' : 'Replay last 8 hours'}
            onClick={() => {
              if (replaying) api.current.stopReplay?.()
              else void api.current.startReplay?.()
            }}
          >
            <History />
          </button>
        </div>
        {replayLabel && <div className="globe-replay-clock">{replayLabel}</div>}
        {!positioned.length && <p>Add your location in Profile</p>}
      </div>
  )

  // a full-bleed stage behind the room chrome: the toolbar's Globe button is
  // the only switch between the tiles and this view
  return (
    <section className="globe-view" aria-label="Live globe">
      {stage}
    </section>
  )
}
